import { useEffect, useState } from "react";
import { Link, useRevalidator, useRouteLoaderData, useSubmit } from "react-router-dom";
import type { SelectedTechnicians, Work } from "./types";
import UserInfo from "../common/userInfo";
import FileSystem from "../common/fileSystem";

export default function WorkDetail() {
    const work = useRouteLoaderData("work") as Work;
    const [workItem, setWorkItem] = useState<Work>(work);
    const submit = useSubmit();
    const { revalidate } = useRevalidator();
    const technicians = Array.isArray(workItem.technicians) ? workItem.technicians as SelectedTechnicians[] : [];

    useEffect(() => {
        setWorkItem(work);
    }, [work]);

    const setWorkItems = (updater: Function) => {
        setWorkItem(currentWork => {
            const nextItems = typeof updater === "function" ? updater([currentWork]) : updater;
            return Array.isArray(nextItems) ? (nextItems[0] ?? currentWork) : currentWork;
        });
    };

    const handleDelete = () => {
        if (!window.confirm("Are you sure to delete this work?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(workItem.id));
        submit(formData, {
            method: "post",
            action: "Delete",
        });
    };

    const handleVoid = () => {
        if (!window.confirm("Are you sure to void this work?")) return;
        let voidReason = window.prompt("reason to void:");
        if (voidReason === null) return;
        while (voidReason === "") {
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if (voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("id", String(workItem.id));
        formData.append("voidReason", voidReason);
        submit(formData, {
            method: "post",
            action: "Void",
        });
    };

    const handleRestore = () => {
        if (!window.confirm("Are you sure to restore this work?")) return;
        const formData = new FormData();
        formData.append("id", String(workItem.id));
        submit(formData, {
            method: "post",
            action: "Restore",
        });
    };

    const fields = [
        { label: "Work ID", value: workItem.id },
        {
            label: "Project",
            value: workItem.projectId ? <Link to={`/Projects/${workItem.projectId}`}>{workItem.projectName}</Link> : workItem.projectName,
        },
        {
            label: "Proposal",
            value: workItem.proposalId ? <Link to={`/Proposals/${workItem.proposalId}`}>{workItem.proposalNumber}</Link> : workItem.proposalNumber,
        },
        {
            label: "Service",
            value: workItem.serviceId ? <Link to={`/Services/${workItem.serviceId}`}>{workItem.serviceName}</Link> : workItem.serviceName,
        },
        { label: "Category", value: workItem.category },
        { label: "Sub Category", value: workItem.subCategory },
        {
            label: "Location",
            value: workItem.location ? (
                <a
                    href={workItem.coords ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workItem.coords)}` : undefined}
                    target="_blank"
                    rel="noreferrer"
                >
                    {workItem.location}
                </a>
            ) : null,
        },
        { label: "Start Time", value: workItem.startTime },
        { label: "End Time", value: workItem.endTime },
        { label: "All Day", value: workItem.allDay },
        { label: "Supervisor", value: <UserInfo id={workItem.supervisorId} name={workItem.supervisorName} /> },
        { label: "Field Supervisor Name", value: workItem.fieldSupervisorName },
        { label: "Field Supervisor Email", value: workItem.fieldSupervisorEmail },
        {
            label: "Site Contact",
            value: workItem.siteContactId ? (
                <Link to={`/Contacts/${workItem.siteContactId}`}>
                    {[workItem.siteContactName, workItem.siteContactMobile].filter(Boolean).join(" - ")}
                </Link>
            ) : (
                [workItem.siteContactName, workItem.siteContactMobile].filter(Boolean).join(" - ")
            ),
        },
        { label: "CAD Required", value: workItem.cadRequired },
        { label: "Report Required", value: workItem.reportRequired },
        { label: "Waive JSA", value: workItem.waiveJSA },
        { label: "Job Tag Location", value: workItem.jobTagLocation },
        { label: "Lead", value: <UserInfo id={workItem.leadId} name={workItem.leadName} /> },
        { label: "Creator", value: <div><UserInfo id={workItem.creatorId} name = {workItem.creatorName}/>{workItem.createdAt}</div> },
        { label: "Updater", value: <div><UserInfo id={workItem.updaterId} name = {workItem.updaterName}/>{workItem.updatedAt}</div> },
        { label: "Void Reason", value: workItem.voidReason, show: !!workItem.voidReason },
    ];

    return (
        <div>
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <table className="table table-fixed me-3">
                    <tbody className="noTableTopBorder">
                        {fields
                            .filter(field => field.value && field.show !== false)
                            .map((field, idx) => (
                                <tr key={idx}>
                                    <th>{field.label}</th>
                                    <td>{field.value}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                <div className="d-flex flex-column flex-grow-1 me-3" style={{ minWidth: "40%", minHeight: "100px" }}>
                    <fieldset className="border rounded flex-grow-1 px-3">
                        <legend className="fw-bold">Description</legend>
                        <span className="cssNotes" dangerouslySetInnerHTML={{ __html: workItem.description || "" }} />
                    </fieldset>
                </div>
            </div>
            {technicians.length ? (
                <div className="px-3 pt-3">
                        <div className="fw-bold h4">Technicians</div>
                        <div className="d-grid gap-2">
                            {technicians.map(technician => (
                                <div
                                    key={technician.userId}
                                    className="border rounded px-3 py-2 d-flex align-items-center justify-content-between gap-3 flex-wrap"
                                >
                                    <div className="d-flex flex-column">
                                        <div>
                                            <UserInfo id={technician.userId} name={technician.userName} />
                                        </div>
                                        <div className="text-muted small">
                                            {[
                                                technician.laborCategory || "",
                                                technician.fleetNumber ? `Fleet ${technician.fleetNumber}${technician.fleetName ? ` - ${technician.fleetName}` : ""}` : "",
                                            ].filter(Boolean).join(" | ") || "No technician details"}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3 flex-wrap small">
                                        <span className={technician.perDiem === "yes" ? "text-success" : "text-muted"}>
                                            Per Diem: {technician.perDiem === "yes" ? "Yes" : "No"}
                                        </span>
                                    <span className={workItem.leadId === technician.userId ? "text-primary fw-bold" : "text-muted"}>
                                            JSA Lead: {workItem.leadId === technician.userId ? "Yes" : "No"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                </div>
            ) : null}
            <div className="px-3 pt-3">
                    <FileSystem
                        item={workItem}
                        fileSystem={[{
                            title: "Files",
                            folderIdLabel: "folderId",
                            folderKey: "workFiles",
                        }]}
                        setItems={setWorkItems}
                        sqlTable="works"
                        itemLabel="work"
                        idLabel="id"
                        filesKey="workFiles"
                        singleFolderId="folderId"
                        readOnly={false}
                        onFilesChanged={revalidate}
                    />
            </div>
            <div className="my-3">
                {workItem.void === "yes" ? (
                    <>
                        <button children="Restore" className="btn btn-success ms-3" onClick={handleRestore} />
                        <button children="Delete" className="btn btn-danger ms-3" onClick={handleDelete} />
                    </>
                ) : (
                    <>
                        <Link children="Edit" to="Edit" className="btn btn-outline-primary ms-3" />
                        <button children="Void" type="button" className="btn btn-danger ms-3" onClick={handleVoid} />
                    </>
                )}
                <Link children="Back" to=".." relative="path" className="btn btn-outline-secondary float-end" />
            </div>
        </div>
    );
}
